import flagsmithSdk from "@flagsmith/flagsmith";
import type { IFlagsmith, IInitConfig } from "@flagsmith/flagsmith/types";

const DEFAULT_APP_CONFIG_FLAG = "portal_organizations";
const DEFAULT_APP_CONFIG_TRAIT = "hostname";

export function getAppConfigFlagName() {
  return (
    import.meta.env.VITE_FLAGSMITH_APP_CONFIG_FLAG || DEFAULT_APP_CONFIG_FLAG
  );
}

export function getAppConfigTraitName() {
  return (
    import.meta.env.VITE_FLAGSMITH_APP_CONFIG_TRAIT || DEFAULT_APP_CONFIG_TRAIT
  );
}

export type InitFlagsmithOptions = Pick<IInitConfig, "onChange" | "onError"> & {
  hostname: string;
};

let flagsmithClient: IFlagsmith = flagsmithSdk;
let initPromise: Promise<IFlagsmith> | null = null;

export function getFlagsmith() {
  return flagsmithClient;
}

export function setFlagsmithClient(client: IFlagsmith) {
  flagsmithClient = client;
  initPromise = null;
}

export function resetFlagsmithClient() {
  flagsmithClient = flagsmithSdk;
  initPromise = null;
}

async function createInitializedClient({
  hostname,
  onChange,
  onError,
}: InitFlagsmithOptions): Promise<IFlagsmith> {
  const environmentID = import.meta.env.VITE_FLAGSMITH_ENVIRONMENT_ID;

  if (!environmentID) {
    throw new Error("VITE_FLAGSMITH_ENVIRONMENT_ID is required");
  }

  const api = import.meta.env.VITE_FLAGSMITH_API_URL;

  await flagsmithClient.init({
    environmentID,
    cacheFlags: !import.meta.env.DEV,
    evaluationContext: {
      identity: {
        transient: true,
        traits: {
          [getAppConfigTraitName()]: hostname,
        },
      },
    },
    ...(api ? { api } : {}),
    onChange,
    onError,
  });

  return flagsmithClient;
}

export async function initFlagsmith(options: InitFlagsmithOptions) {
  if (!initPromise) {
    initPromise = createInitializedClient(options).catch((error) => {
      initPromise = null;
      throw error;
    });
  }

  return initPromise;
}

export function parseFlagJson(raw: unknown): unknown {
  if (raw == null || raw === "") {
    return null;
  }

  if (typeof raw === "object") {
    return raw;
  }

  if (typeof raw !== "string") {
    return null;
  }

  const trimmed = raw.trim();

  try {
    return JSON.parse(trimmed);
  } catch {
    const start = trimmed.indexOf("{");
    const end = trimmed.lastIndexOf("}");

    if (start < 0 || end <= start) {
      return null;
    }

    try {
      return JSON.parse(trimmed.slice(start, end + 1));
    } catch {
      return null;
    }
  }
}

export function getAppConfigFlag(): unknown {
  return parseFlagJson(
    getFlagsmith().getValue(getAppConfigFlagName(), {
      fallback: null,
    })
  );
}
